// File: frontend/src/services/adminDashboardService.ts

import { apiClient } from "./apiClient";

const BASE_URL = "/api/admin/dashboard";

// ── Types ──────────────────────────────────────────────────────────────────

export interface DashboardRecentUser {
  id: number;
  username: string;
  role: string;
  registrationDate: string;
}

export interface DashboardRecentOnlineUser {
  id: number;
  username: string;
  role: string;
  /** Last successful login — the only activity timestamp the backend persists. */
  lastLoginDate: string;
  /** True while the user still has a live WebSocket session. */
  online: boolean;
}

export interface DashboardRecentEntry {
  id: number;
  contentPreview: string;
  authorId: number;
  authorUsername: string;
  // Match entry fields (non-null when entry belongs to a match)
  matchId: number | null;
  homeTeam: string;
  awayTeam: string;
  matchDate: string | null;
  // Club entry fields (non-null when entry belongs to a club page)
  clubSlug: string | null;
  clubLabel: string | null;
  // Team entry fields (non-null when entry belongs to a team page)
  teamClubSlug: string | null;
  teamSportSlug: string | null;
  teamLabel: string | null;
  // Person entry fields (non-null when entry belongs to a person page)
  personSlug: string | null;
  personLabel: string | null;
  // Custom topic entry fields (non-null when entry belongs to a gündem page)
  topicSlug: string | null;
  topicLabel: string | null;
  createdAt: string;
}

// DashboardStatsDto now only carries the recent-activity lists — total/pending/online
// counts live on DashboardKpi (fetched separately) and were unused duplicates here.
export interface DashboardStats {
  recentOnlineUsers: DashboardRecentOnlineUser[];
  recentUsers: DashboardRecentUser[];
  recentEntries: DashboardRecentEntry[];
}

/** Returned by the /kpi endpoint — counts filtered to a period. */
export interface DashboardKpi {
  period: string;
  users: number;
  // User breakdown by role
  rookieUsers: number;
  authorUsers: number;
  moderatorUsers: number;
  adminUsers: number;
  // Total entries (all types combined)
  entries: number;
  // Entry breakdown by subject type
  matchEntries: number;
  personEntries: number;
  clubEntries: number;
  teamEntries: number;
  customTopicEntries: number;
  // Entity counts
  matches: number;
  clubs: number;
  teams: number;
  persons: number;
  topics: number;
  // Live values — unaffected by period
  onlineUsers: number;
  pendingReports: number;
  pendingTopicSuggestions: number;
  // Entry action counts — all-time totals, unaffected by period
  userDeletedEntries: number;
  userEditedEntries: number;
  modAdminDeletedEntries: number;
  modAdminEditedEntries: number;
  // Live theme-usage breakdown — unaffected by period. One entry per theme
  // (declaration order), even when a theme currently has 0 users.
  themeCounts: DashboardThemeCount[];
  // User breakdown by lifecycle status. Period-filtered on the status change
  // date (not the registration date), so a user suspended today shows up under
  // "Bugün" regardless of when they registered — which also means this list
  // does not sum up to `users`. One entry per UserStatus enum value
  // (declaration order), even when a status currently has 0 users.
  userStatusCounts: DashboardUserStatusCount[];
}

export interface DashboardThemeCount {
  theme: string;
  userCount: number;
}

export interface DashboardUserStatusCount {
  status: string;
  userCount: number;
}

export interface DashboardChartPoint {
  label: string;
  value: number;
}

export type DashboardKpiPeriod   = "total" | "today" | "week" | "month" | "thisyear";
export type DashboardChartPeriod = "today" | "week" | "month" | "year" | "thisyear" | "alltime";
export type DashboardMetric      = "entries" | "users" | "matches" | "clubs" | "topics";
export type DashboardChartType   = "area" | "line" | "bar";

// ── API calls ──────────────────────────────────────────────────────────────

/**
 * Fetches the "recent activity" lists (5 most recently online users +
 * 5 newest users + 5 newest entries).
 * Called once on dashboard mount; does not change with the KPI period selector.
 */
export const fetchDashboardStats = (): Promise<DashboardStats> =>
  apiClient.get<DashboardStats>(`${BASE_URL}/stats`);

/**
 * Fetches period-filtered counts for all KPI metric cards.
 *
 * @param period "total" | "today" | "week" | "month"
 */
export const fetchDashboardKpi = (period: DashboardKpiPeriod): Promise<DashboardKpi> => {
  const params = new URLSearchParams({ period });
  return apiClient.get<DashboardKpi>(`${BASE_URL}/kpi?${params}`);
};

/**
 * Fetches a time-series data array for the given metric and chart period.
 *
 * @param metric "entries" | "users" | "matches" | "clubs"
 * @param period "today" | "week" | "month" | "year" | "alltime"
 */
export const fetchDashboardChart = (
  metric: DashboardMetric,
  period: DashboardChartPeriod
): Promise<DashboardChartPoint[]> => {
  const params = new URLSearchParams({ metric, period });
  return apiClient.get<DashboardChartPoint[]>(`${BASE_URL}/chart?${params}`);
};